<script setup>
import { ref } from 'vue';
import axios from 'axios';
import { useAlert } from 'dashboard/composables';
import Header from './components/Header.vue';
import Footer from './components/Footer.vue';

const name = ref('');
const email = ref('');
const companySize = ref('1-50 employees');
const topic = ref('Sales Inquiry');
const message = ref('');
const consent = ref(false);
const isSubmitting = ref(false);

const submitInquiry = async () => {
  if (!consent.value) {
    useAlert('Please agree to the Privacy Policy');
    return;
  }

  isSubmitting.value = true;
  try {
    await axios.post('/public/api/v1/contact_inquiries', {
      contact_inquiry: {
        name: name.value,
        email: email.value,
        company_size: companySize.value,
        topic: topic.value,
        message: message.value,
      }
    });
    useAlert('Your message has been sent successfully!');
    // Reset form
    name.value = '';
    email.value = '';
    companySize.value = '1-50 employees';
    topic.value = 'Sales Inquiry';
    message.value = '';
    consent.value = false;
  } catch (error) {
    useAlert(error.response?.data?.error || 'Failed to send message. Please try again.');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="scroll-smooth">
    <!-- Top Navigation Bar -->
    <Header />

    <!-- Hero Section with Background Shader -->
    <section
      class="relative pt-24 pb-16 px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto overflow-hidden"
    >
      <div class="absolute inset-0 -z-10 opacity-30" />
      <div class="max-w-3xl">
        <h1
          class="font-display-xl text-display-xl md:text-display-xl text-primary leading-tight mb-6"
        >
          Let's architect your <span class="text-secondary">AI future.</span>
        </h1>
        <p class="font-body-lg text-body-lg text-on-surface-variant max-w-xl">
          Whether you're scaling a global helpdesk or just starting your AI
          journey, our team of experts is ready to assist.
        </p>
      </div>
    </section>
    <!-- Main Content Bento Grid -->
    <section
      class="px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto pb-stack-xl"
    >
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-gutter">
        <!-- Left Column: Sales Inquiry Form -->
        <div class="lg:col-span-7">
          <div class="glass-card rounded-xl p-8 md:p-12 h-full shadow-sm">
            <div class="mb-10">
              <h2 class="font-headline-md text-headline-md text-deep-navy mb-2">
                Send a Message
              </h2>
              <p class="text-on-surface-variant">
                Our sales engineers usually respond within 2 business hours.
              </p>
            </div>
            <form class="space-y-6" @submit.prevent="submitInquiry">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="space-y-2 ai-glow-border group">
                  <label
                    class="font-label-md text-label-md text-on-surface-variant block ml-1"
                    >Full Name</label>
                  <input
                    v-model="name"
                    class="w-full bg-white border border-outline-variant rounded-lg px-4 py-3 focus:ring-0 focus:border-secondary outline-none transition-all placeholder:text-outline/50"
                    placeholder="Jane Doe"
                    type="text"
                    required
                  />
                </div>
                <div class="space-y-2 ai-glow-border group">
                  <label
                    class="font-label-md text-label-md text-on-surface-variant block ml-1"
                    >Work Email</label>
                  <input
                    v-model="email"
                    class="w-full bg-white border border-outline-variant rounded-lg px-4 py-3 focus:ring-0 focus:border-secondary outline-none transition-all placeholder:text-outline/50"
                    placeholder="jane@company.com"
                    type="email"
                    required
                  />
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="space-y-2 ai-glow-border group">
                  <label
                    class="font-label-md text-label-md text-on-surface-variant block ml-1"
                    >Company Size</label>
                  <select
                    v-model="companySize"
                    class="w-full bg-white border border-outline-variant rounded-lg px-4 py-3 focus:ring-0 focus:border-secondary outline-none transition-all text-on-surface-variant"
                  >
                    <option>1-50 employees</option>
                    <option>51-200 employees</option>
                    <option>201-1000 employees</option>
                    <option>1000+ employees</option>
                  </select>
                </div>
                <div class="space-y-2 ai-glow-border group">
                  <label
                    class="font-label-md text-label-md text-on-surface-variant block ml-1"
                    >Topic</label>
                  <select
                    v-model="topic"
                    class="w-full bg-white border border-outline-variant rounded-lg px-4 py-3 focus:ring-0 focus:border-secondary outline-none transition-all text-on-surface-variant"
                  >
                    <option>Sales Inquiry</option>
                    <option>Partnership</option>
                    <option>Product Demo</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
              <div class="space-y-2 ai-glow-border group">
                <label
                  class="font-label-md text-label-md text-on-surface-variant block ml-1"
                  >How can we help?</label>
                <textarea
                  v-model="message"
                  class="w-full bg-white border border-outline-variant rounded-lg px-4 py-3 focus:ring-0 focus:border-secondary outline-none transition-all placeholder:text-outline/50"
                  placeholder="Tell us about your support challenges..."
                  rows="5"
                  required
                />
              </div>
              <div class="flex items-center gap-3 py-2">
                <input
                  id="consent"
                  v-model="consent"
                  class="w-5 h-5 rounded border-outline-variant text-secondary focus:ring-secondary"
                  type="checkbox"
                  required
                />
                <label
                  class="text-caption text-on-surface-variant"
                  for="consent"
                >
                  I agree to OS DESK's
                  <a class="underline" href="#">Privacy Policy</a> and consent
                  to receiving marketing communications.
                </label>
              </div>
              <button
                :disabled="isSubmitting"
                class="w-full md:w-auto bg-gradient-to-r from-primary to-deep-navy text-white px-10 py-4 rounded-lg font-headline-md text-headline-md hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ isSubmitting ? 'Sending...' : 'Send Inquiry' }}
              </button>
            </form>
          </div>
        </div>
        <!-- Right Column: Support, Press, and Office -->
        <div class="lg:col-span-5 space-y-gutter">
          <!-- Support Portal Card -->
          <div
            class="bg-ai-glow border border-secondary/20 rounded-xl p-8 flex flex-col justify-between group hover:border-secondary/40 transition-all cursor-pointer"
          >
            <div>
              <div
                class="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-6 text-secondary"
              >
                <span class="material-symbols-outlined icon-fill">contact_support</span>
              </div>
              <h3 class="font-headline-md text-headline-md text-deep-navy mb-2">
                Technical Support
              </h3>
              <p class="text-on-surface-variant mb-6">
                Existing customer? Access our 24/7 AI-powered support portal and
                documentation.
              </p>
            </div>
            <router-link
              class="flex items-center text-secondary font-semibold group-hover:gap-2 transition-all"
              to="/help-center"
            >
              Visit Support Center
              <span class="material-symbols-outlined ml-1">arrow_forward</span>
            </router-link>
          </div>
          <!-- Press Card -->
          <div
            class="bg-surface-container-low border border-outline-variant/30 rounded-xl p-8"
          >
            <div class="flex items-start gap-4">
              <div
                class="w-12 h-12 bg-white rounded-full flex items-center justify-center text-primary-container shrink-0 border border-outline-variant/20"
              >
                <span class="material-symbols-outlined">campaign</span>
              </div>
              <div>
                <h3
                  class="font-headline-md text-headline-md text-deep-navy mb-1"
                >
                  Press &amp; Media
                </h3>
                <p class="text-on-surface-variant mb-4">
                  Inquiries for interviews, media assets, or brand guidelines.
                </p>
                <a
                  class="text-primary font-medium hover:underline"
                  href="mailto:press@onlinesavaari.com"
                  >press@onlinesavaari.com</a>
              </div>
            </div>
          </div>
          <!-- Location Card -->
          <div
            class="bg-white border border-outline-variant/30 rounded-xl overflow-hidden flex flex-col h-[400px]"
          >
            <div class="p-8 pb-4">
              <h3 class="font-headline-md text-headline-md text-deep-navy mb-1">
                Global Headquarters
              </h3>
              <p class="text-on-surface-variant">
                302 range Heights New Link Road Jogeswari West Mumbai INDIA 400102
              </p>
            </div>
            <div
              class="flex-grow relative mt-2 grayscale hover:grayscale-0 transition-all duration-700 overflow-hidden"
            >
              <div
                class="w-full h-full bg-surface-container-highest flex items-center justify-center overflow-hidden"
                data-location="Mumbai"
                style=""
              >
                <div class="absolute inset-0 opacity-40">
                  <div
                    class="w-full h-full bg-cover bg-center"
                    data-alt="A stylized, architectural map of Mumbai's downtown district in a minimalist light mode aesthetic. The map uses thin, precise slate grey lines on a soft off-white background. No labels or text are visible, emphasizing the geometric grid and urban planning. The lighting is bright and clean, consistent with a professional enterprise UI."
                    style="
                      background-image: url('/brand-assets/portfolio/stitch_4d884453c4c0.jpg');
                    "
                  />
                </div>
                <div
                  class="relative z-10 w-4 h-4 bg-secondary rounded-full shadow-[0_0_20px_rgba(0,109,64,0.6)] animate-pulse"
                />
                <div
                  class="absolute z-10 bottom-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded text-caption font-bold border border-secondary/20"
                >
                  OS DESK HQ
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>


    <!-- Footer -->
    <Footer />
  </div>
</template>
